import type { ChatRoomSummary } from '@wtfalch/chat';
import { parseCallNotice } from '../calls/callNotice.ts';

import { DAY_MS, MONTHS, startOfDay, WEEKDAYS } from '../ui/format.ts';
import type { ChatTimelineEvent } from './types.ts';

/**
 * The pure pieces of the Chat section: sorting the room list, and turning a
 * room's events into the rows the stream draws. Kept apart from the
 * components so they can be tested without a client, a DOM, or a network --
 * `formatWhen` (a message's own timestamp) already lives in `ui/format.ts`
 * and is reused as-is; this file only adds what Chat alone needs.
 */

/** A room's own group: an unanswered invitation, a channel (not direct, not
 *  an invitation), or a direct message. */
export type RoomGroups = {
  invitations: ChatRoomSummary[];
  channels: ChatRoomSummary[];
  directs: ChatRoomSummary[];
};

/** Three lists, each in `rooms`' own order -- `ChatClient.rooms()` does not
 *  promise an order of its own, so nothing here re-sorts within a group
 *  beyond what `sortRooms` already did to the list handed in. */
export function groupRooms(rooms: readonly ChatRoomSummary[]): RoomGroups {
  const invitations: ChatRoomSummary[] = [];
  const channels: ChatRoomSummary[] = [];
  const directs: ChatRoomSummary[] = [];
  for (const room of rooms) {
    if (room.membership === 'invite') invitations.push(room);
    else if (room.isDirect) directs.push(room);
    else channels.push(room);
  }
  return { invitations, channels, directs };
}

/** "Unread first": every room with something unread, in its own given
 *  order, ahead of every room without -- a stable partition, not a sort by
 *  count, so two unread rooms never swap places just because one has more.
 *  `false` returns `rooms` untouched (same reference, even), the room list's
 *  default. */
export function sortRooms(
  rooms: readonly ChatRoomSummary[],
  unreadFirst: boolean,
): readonly ChatRoomSummary[] {
  if (!unreadFirst) return rooms;
  const unread = rooms.filter((r) => r.unreadCount > 0);
  const read = rooms.filter((r) => r.unreadCount === 0);
  return [...unread, ...read];
}

export type ListView = 'all' | 'unread' | 'mentions';

/** What the room list's select shows: every room, unread ones first, or only
 *  the rooms where somebody tagged the person. */
export function viewRooms(
  rooms: readonly ChatRoomSummary[],
  view: ListView,
): readonly ChatRoomSummary[] {
  if (view === 'mentions') return rooms.filter((r) => mentionCount(r) > 0);
  return sortRooms(rooms, view === 'unread');
}

/** The one line a room row draws under its name for `ChatRoomSummary.
 *  lastMessage` -- the same shape `@wtfalch/email`'s own thread list draws
 *  under a subject. "You: <body>" when the signed-in person sent it
 *  (`mine`, checked first since it says more than the room's own shape
 *  does); otherwise "<senderName>: <body>" in a room with more than the
 *  two of you to attribute it to, or the bare body alone in a direct room,
 *  where there is only ever one other person it could be from. `null` when
 *  `lastMessage` itself is `null` -- nothing sent yet, or an unanswered
 *  invitation -- so a row draws no line at all. */
export function lastMessageLabel(room: ChatRoomSummary): string | null {
  const { lastMessage } = room;
  if (!lastMessage) return null;
  if (parseCallNotice(lastMessage.body) !== null) return 'Call started';
  if (lastMessage.mine) return `You: ${lastMessage.body}`;
  return room.isDirect ? lastMessage.body : `${lastMessage.senderName}: ${lastMessage.body}`;
}

/** The subset of `unreadCount` that names this person -- Matrix's own
 *  `unread_notifications.highlight_count` from `/sync`, which
 *  `ChatRoomSummary` (`@wtfalch/chat` 0.5.0) does not surface yet
 *  (wtfalch/chat#45). A room row draws weight (bold) off `unreadCount >
 *  0` and a badge off this -- "something was said" against "you were
 *  tagged", the distinction agora#295 found missing. Read as an optional
 *  field rather than hard-coded to zero, under both names the SDK might
 *  land it under, so the badge starts drawing the day it ships with no
 *  change here. */
export function mentionCount(room: ChatRoomSummary): number {
  const withMentions = room as ChatRoomSummary & {
    readonly mentionCount?: number;
    readonly highlightCount?: number;
  };
  return withMentions.mentionCount ?? withMentions.highlightCount ?? 0;
}

/** A room's unread count as drawn in the list: the number, or `99+` past it. */
export function unreadCountLabel(count: number): string {
  return count > 99 ? '99+' : String(count);
}

function dayKey(ts: number): string {
  const d = new Date(ts);
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/** "Today", "Yesterday", a weekday within the last week, or a short date
 *  beyond that -- the same ladder `ui/format.ts`'s `formatWhen` uses for a
 *  row's timestamp, capitalised for a divider's own word rather than a
 *  clock beside a name. */
function dayLabel(ts: number, now: Date): string {
  const at = new Date(ts);
  const days = Math.floor((startOfDay(now) - startOfDay(at)) / DAY_MS);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  if (days > 1 && days < 7) return WEEKDAYS[at.getDay()] ?? '';
  return `${at.getDate()} ${MONTHS[at.getMonth()] ?? ''}`;
}

export type TimelineRow =
  | { kind: 'divider'; key: string; label: string }
  | { kind: 'new'; key: string }
  | { kind: 'message'; key: string; event: ChatTimelineEvent; collapsed: boolean };

/** A message within this of the one before it, from the same sender,
 *  collapses under it -- "a few minutes," left as a round five, the same
 *  window most chat clients settle on. */
const COLLAPSE_WINDOW_MS = 5 * 60 * 1000;

/**
 * `events`, oldest first, as rows: a day divider wherever the calendar day
 * changes, and -- once, right after the last event at or before
 * `readUpToId` -- a "New" separator. Nothing placed after the newest event:
 * unread past the last message there is not a fact a reader needs told, and
 * nothing is placed if `readUpToId` is the newest event already (nothing
 * unread) or is not found in `events` at all (its position is not known).
 *
 * Each message row also says whether it collapses under the one before it:
 * the same sender, within `COLLAPSE_WINDOW_MS`, with no divider or "new"
 * marker between them -- either one always breaks a run, so a reader is
 * never left guessing who a body belongs to across a day boundary.
 */
export function layoutTimeline(
  events: readonly ChatTimelineEvent[],
  readUpToId: string | null,
  now: Date,
): TimelineRow[] {
  const rows: TimelineRow[] = [];
  let lastDay: string | null = null;
  let lastMessage: ChatTimelineEvent | null = null;
  const newAt = readUpToId ? events.findIndex((e) => e.id === readUpToId) : -1;
  for (let i = 0; i < events.length; i++) {
    const event = events[i];
    if (!event) continue;
    const day = dayKey(event.ts);
    if (day !== lastDay) {
      rows.push({ kind: 'divider', key: `divider-${day}`, label: dayLabel(event.ts, now) });
      lastDay = day;
      lastMessage = null;
    }
    const collapsed = collapses(lastMessage, event);
    rows.push({ kind: 'message', key: event.id, event, collapsed });
    lastMessage = event;
    if (i === newAt && i < events.length - 1) {
      rows.push({ kind: 'new', key: 'new' });
      lastMessage = null;
    }
  }
  return rows;
}

function collapses(prev: ChatTimelineEvent | null, event: ChatTimelineEvent): boolean {
  if (!prev) return false;
  if (prev.senderId !== event.senderId) return false;
  return event.ts - prev.ts <= COLLAPSE_WINDOW_MS;
}

/** A file's size the way a file card words it: "14 KB", "2.3 MB"; empty when
 *  the sender reported none. */
export function fileSize(bytes: number): string {
  if (bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** The short type a file row's pill reads: its extension ("PNG"), else the
 *  mime subtype. */
export function fileType(name: string, mimeType: string): string {
  const dot = name.lastIndexOf('.');
  const ext = dot > 0 ? name.slice(dot + 1) : '';
  return (ext || mimeType.split('/')[1] || 'file').slice(0, 5).toUpperCase();
}

/** A id like `@agora-preview-abc:chat.example.test` is not a name --
 *  `ChatTimelineEvent.senderName` and `ChatThreadSummary.participants` both
 *  fall back to the bare Matrix id when nobody has set a display name, and
 *  that raw string is never shown as one. The localpart (after the `@`,
 *  before the first `:`) reads the way a name would, even though it is not
 *  one; a real display name, which never starts with `@`, passes through
 *  unchanged. */
export function shortName(nameOrId: string): string {
  if (!nameOrId.startsWith('@')) return nameOrId;
  const withoutAt = nameOrId.slice(1);
  const colon = withoutAt.indexOf(':');
  return colon === -1 ? withoutAt : withoutAt.slice(0, colon);
}

/** A guest's end date worded the way `Message`'s `guestUntil` and the member
 *  list read it -- "20 Oct" -- or `undefined` for an ordinary member, so
 *  presence of the label is the whole signal. An `until` that cannot be parsed
 *  has already ended (docs/guests.md, "Failing closed"), so it reads as "now"
 *  rather than as no guest at all. */
export function guestUntilLabel(guest: { until: string } | undefined): string | undefined {
  if (!guest) return undefined;
  const at = new Date(guest.until);
  if (Number.isNaN(at.getTime())) return 'now';
  return `${at.getDate()} ${MONTHS[at.getMonth()] ?? ''}`;
}
